import { useMemo, useState } from 'react';
import { MockNote } from '@/components/foundations';
import { AppShell, PageTitle } from '@/components/layout';
import { Button, SearchInput, Select } from '@/components/forms';
import { Icon } from '@/components/foundations';
import { CATEGORY_OPTIONS, FIN_TX, brl } from '@/mocks/financas';
import { Badge, DataTable, InstallmentBadge, KeyValueList, Pagination, TableFilters } from '@/components/data';
import { DetailDrawer, DrawerLayout, DrawerSection, StatusHero } from '@/components/feedback';
import { Amount } from './parts';
import { CategoryPicker, RuleBox } from './components';

/** Extrato com filtros + painel de detalhes (recategorizar e criar regra) — mock, nada é salvo. */
export function FinExtratoPage() {
  const [sel, setSel] = useState<number | null>(2);
  const [q, setQ] = useState('');
  const [acc, setAcc] = useState('');
  const [kind, setKind] = useState('');
  const [cats, setCats] = useState<Record<string, string>>({});
  const [rule, setRule] = useState('LOJA ELETRO EXEMPLO');
  const [msg, setMsg] = useState('');
  const rows = useMemo(
    () =>
      FIN_TX.map((t, i) => ({ t: { ...t, category: cats[t.id] ?? t.category }, i })).filter(
        ({ t }) =>
          (!q || t.desc.toLowerCase().includes(q.toLowerCase())) &&
          (!acc || t.account.startsWith(acc)) &&
          (!kind || (kind === 'parc' ? !!t.installment : !t.installment)),
      ),
    [q, acc, kind, cats],
  );
  const t = sel === null ? null : { ...FIN_TX[sel], category: cats[FIN_TX[sel].id] ?? FIN_TX[sel].category };
  const out = rows.filter((r) => r.t.amount < 0).reduce((s, r) => s + r.t.amount, 0);
  const inc = rows.filter((r) => r.t.amount > 0).reduce((s, r) => s + r.t.amount, 0);
  return (
    <AppShell stackHelp>
      <DrawerLayout
        open={!!t}
        drawer={
          <DetailDrawer title="Detalhes do lançamento" open={!!t} onClose={() => setSel(null)}>
            {t && (
              <>
                <StatusHero tone={t.status === 'POSTED' ? 'ok' : 'warn'} label={t.status === 'POSTED' ? 'Lançada' : 'Pendente'} amount={t.amount} />
                <DrawerSection icon="card" title="Informações">
                  <KeyValueList
                    items={[
                      { k: 'Descrição', v: t.desc },
                      { k: 'Data', v: t.date },
                      ...(t.installment ? [{ k: 'Parcela', v: `${t.installment} · total ${brl(t.total ?? 0)}` }] : []),
                      ...(t.bill ? [{ k: 'Fatura', v: t.bill }] : []),
                      { k: 'Categoria Pluggy', v: t.pluggyCategory, mono: true },
                    ]}
                  />
                </DrawerSection>
                <DrawerSection icon="tag" title="Categoria">
                  <CategoryPicker options={CATEGORY_OPTIONS} value={t.category} onChange={(c) => setCats({ ...cats, [t.id]: c })} style={{ paddingLeft: 34 }} />
                  <RuleBox
                    pattern={rule}
                    onPatternChange={setRule}
                    category={t.category}
                    affectedCount={7}
                    onCreate={() => setMsg('Regra criada (demonstração).')}
                    onOnlyThis={() => setMsg('Só este lançamento foi alterado.')}
                    message={msg}
                  />
                </DrawerSection>
              </>
            )}
          </DetailDrawer>
        }
      >
          <PageTitle variant="page" size="lg" title="Extrato" subtitle="Conta e cartão do Inter, organizados do seu jeito." actions={<Button size="lg" icon="download">Exportar</Button>} />
          <TableFilters>
            <SearchInput label="Buscar" placeholder="Buscar descrição ou loja" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select label="Período" defaultValue="" placeholder="Outubro 2026" options={[{ value: 'Setembro 2026', label: 'Setembro 2026' }]} />
            <Select label="Conta" value={acc} onChange={(e) => setAcc(e.target.value)} placeholder="Conta" options={[{ value: 'Cartão', label: 'Cartão •••• 0597' }, { value: 'Conta', label: 'Conta Inter' }]} />
            <Select label="À vista ou parcelado" value={kind} onChange={(e) => setKind(e.target.value)} placeholder="À vista / Parcelado" options={[{ value: 'vista', label: 'À vista' }, { value: 'parc', label: 'Parcelado' }]} />
          </TableFilters>
          <section className="card tx-table">
            <DataTable
              rows={rows}
              rowKey={({ t: r }) => r.id}
              onRowClick={({ t: r, i }) => { setSel(i); setRule(r.desc); setMsg(''); }}
              isSelected={({ i }) => sel === i}
              columns={[
                { key: 'date', header: 'Data', render: ({ t: r }) => r.date },
                { key: 'desc', header: 'Descrição', cellClassName: 'id wrap', render: ({ t: r }) => <>{r.desc} {r.installment && <InstallmentBadge value={r.installment} />}</> },
                { key: 'cat', header: 'Categoria', hideOnMobile: true, render: ({ t: r }) => <Badge tone={r.category === 'Sem categoria' ? 'err' : r.amount > 0 ? 'ok' : 'neutral'}>{r.category}</Badge> },
                { key: 'acc', header: 'Conta', hideOnMobile: true, render: ({ t: r }) => r.account },
                { key: 'val', header: 'Valor', cellClassName: 'val', render: ({ t: r }) => <Amount v={r.amount} /> },
                { key: 'go', header: '', align: 'right', hideOnMobile: true, render: () => <Icon name="chev" className="chev-c" /> },
              ]}
            />
            <Pagination page={1} pageCount={1} summary={<>{rows.length} lançamentos · entradas {brl(inc)} · saídas {brl(out)}</>} />
          </section>
          <MockNote />
      </DrawerLayout>
    </AppShell>
  );
}
